import {
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import saveAs from "file-saver";
import qs from "qs";
import config from "../config.json";
import { IPageable, Params, Option } from "../types/table/interfaces.type";
import { IProvider } from "./ProviderService";
import { createAxiosInstance } from "./ServicesHelpers";
import { CostPlanType } from "./CostPlanService";

const axiosInstance = createAxiosInstance(config.ADMIN_API_BASE_URL);

export enum BulkRateRouteRequestType {
  RATE = "RATE",
  ROUTE = "ROUTE",
  RATE_ROUTE = "RATE_ROUTE",
}

export type BulkRateRouteRequest = {
  type: BulkRateRouteRequestType;
  rateType: RateType;
  rateModifier: number;
  providerId: IProvider["id"];
  provider1Id: IProvider["id"];
  provider2Id: IProvider["id"];
  provider3Id: IProvider["id"];
  percentage0: number;
  percentage1: number;
  percentage2: number;
  percentage3: number;
};

export enum BulkMnpHlrRequestType {
  MNP = "MNP",
  HLR = "HLR",
  MNP_HLR = "MNP_HLR",
}

export type BulkMnpHlrRequest = {
  type: BulkMnpHlrRequestType;
  mnpServiceProviderId?: number | null;
  hlrServiceProviderId?: number | null;
};

export enum RateType {
  VALUE = "VALUE",
  MULTIPLE_OF_COST = "MULTIPLE_OF_COST",
  ADD_TO_COST = "ADD_TO_COST",
}

export interface RoutingPlanDetailsType {
  id: string;
  name: string;
  routingPlanId: string | string[];
  providerId: string;
  operatorId: number;
  rate?: number;
  profit?: number;
  cost?: number;
  rateType?: RateType;
  rateModifier?: number;
  formulaRate?: number;
  formulaProfit?: number;
  mnpCost: number;
  hlrCost: number;
  lastRate?: number;
  type?: string;
  effectiveDate?: Date;
  isDeleted?: boolean;
  rateChange?: string;
  hlrServiceProviderId?: string;
  mnpServiceProviderId?: string;
  createdDate?: Date;
  createdBy?: string;
  provider1Id?: string;
  percentage0?: number;
  percentage1?: number;
  provider2Id?: string;
  percentage2?: number;
  provider3Id?: string;
  percentage3?: number;
  isSent?: boolean;
  historyTimestamp: Date;
  updatedBy?: string;
  convertedCost?: number;
  convertedMnpCost?: number;
  convertedHlrCost?: number;
}

export interface RoutingPlanDetailsHistory {
  id: string;
  snapshot: RoutingPlanDetailsType & { timestamp: string };
  historyTimestamp: string;
  mainId: number;
  createdBy: string;
}

export interface IBulkCustomer {
  id: string;
  routingPlanId: string;
  customerId: number;
}

export const exportRoutingPlanDetails = async (
  params: Params<RoutingPlanDetailsType> = {},
): Promise<void> => {
  try {
    const response = await axiosInstance.get("/rated-routing/csv", {
      params,
      responseType: "blob",
    });

    const disposition = response.headers["content-disposition"];
    let filename = "RoutingPlanDetails.csv";
    if (disposition) {
      const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match && match[1]) {
        filename = match[1].replace(/['"]/g, "").trim();
      }
    }

    const blob = new Blob([response.data], { type: "text/csv" });
    saveAs(blob, filename);

    console.log("Export successful and file downloaded.");
  } catch (error: any) {
    console.error(
      "Error fetching RoutingPlanDetails :",
      error.response?.data?.message || error.message || error,
    );
  }
};

export async function fetchRoutingPlanDetails(
  params: Params<RoutingPlanDetailsType>,
): Promise<IPageable<RoutingPlanDetailsType>> {
  try {
    const response = await axiosInstance.get("/rated-routing/advanced-search", {
      params,
    });
    const data = response.data;
    return data;
  } catch (error: any) {
    console.error("Error fetching routing plan details:", error);
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while getting the rated-routing details.",
    );
  }
}

export async function fetchRoutingPlanDetailsCount(
  params: Params<RoutingPlanDetailsType>,
): Promise<number> {
  try {
    const response = await axiosInstance.get("/rated-routing/count", {
      params,
    });
    const data = response.data;
    return data;
  } catch (error: any) {
    console.error("Error fetching routing plan details count:", error);
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while getting the rated-routing details.",
    );
  }
}
export async function fetchRoutingPlanDetail(
  id: RoutingPlanDetailsType["id"],
): Promise<RoutingPlanDetailsType> {
  try {
    const response = await axiosInstance.get("/rated-routing/" + id);
    const data = response.data;
    return data;
  } catch (error: any) {
    console.error("Error fetching routing plan details:", error);
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while getting the rated-routing details.",
    );
  }
}

export interface LiveCostParams {
  operatorIds?: number[];
  routingPlanId?: string;
  providerId?: number;
  provider1Id?: number;
  percentage1?: number;
  provider2Id?: number;
  percentage2?: number;
  provider3Id?: number;
  percentage3?: number;
  hlrServiceProviderId?: number;
  mnpServiceProviderId?: number;
}

export interface LiveCostResponseItem {
  operatorId: number;
  cost: number;
  convertedCost: number;
  mnpCost: number;
  convertedMnpCost: number;
  hlrCost: number;
  convertedHlrCost: number;
  totalConvertedCost: number;
}

export async function fetchLiveCost(
  params: LiveCostParams,
  signal?: AbortSignal,
): Promise<LiveCostResponseItem[]> {
  try {
    const response = await axiosInstance.get<LiveCostResponseItem[]>(
      "/rated-routing/live-cost",
      {
        params,
        signal,
        paramsSerializer: (p) => qs.stringify(p, { arrayFormat: "repeat" }),
      },
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while calculating the live cost.",
    );
  }
}

export const addRoutingPlanDetailsApi = async (
  data: Partial<RoutingPlanDetailsType>,
) => {
  try {
    const response = await axiosInstance.post("/rated-routing", data);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while adding the rated-routing.",
    );
  }
};
export const RoutingPlanDetailsBulkApi = async (
  data: Partial<RoutingPlanDetailsType>[],
) => {
  try {
    const Data = data.map(({ id, ...rest }) => rest);
    const response = await axiosInstance.put("/rated-routing", Data);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while adding the rated-routing.",
    );
  }
};

export const editRoutingPlanDetailsApi = async (
  id: string,
  data: Partial<RoutingPlanDetailsType>,
) => {
  try {
    const response = await axiosInstance.put(`/rated-routing/${id}`, data);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while updating the rated-routing.",
    );
  }
};

export const CustomerBulkApi = async (
  routingPlanId: string | string[],
  customerIds: number[],
) => {
  try {
    const response = await axiosInstance.put(
      `/routing-plans/${routingPlanId}/customers`,
      { clientIds: customerIds },
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while updating the routing plan customers.",
    );
  }
};

export const getRoutingPlanDetailsById = async (id: string) => {
  try {
    const response = await axiosInstance.get(`/rated-routing/${id}`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching the rated-routing.",
    );
  }
};

export const deleteRoutingPlanDetailsApi = async (
  ids: string[],
): Promise<void> => {
  try {
    await axiosInstance.delete(`/rated-routing`, { data: ids });
    console.log(`Routing plan with ID ${ids} deleted successfully.`);
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while deleting the rated-routing.",
    );
  }
};

export const sendRateNotificationApi = async (
  routingPlanIds: string[],
  testEmail?: string,
  sendonlyChange = true,
  updateLastRate = true,
  attachCcEmail?: boolean,
): Promise<any> => {
  try {
    const body: {
      routingPlanIds: string[];
      testEmail?: string;
      sendonlyChange: boolean;
      updateLastRate: boolean;
      attachCcEmail?: boolean;
    } = {
      routingPlanIds,
      sendonlyChange,
      updateLastRate,
    };
    if (testEmail) {
      body.testEmail = testEmail;
    }
    if (attachCcEmail !== undefined) {
      body.attachCcEmail = attachCcEmail;
    }

    const response = await axiosInstance.post(
      "/rated-routing/rate/notification",
      body,
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while sending emails.",
    );
  }
};

export interface RateCsvRecord {
  operator: string;
  country: string;
  mnc: string;
  mcc: string;
  cc: string;
  oldPrice: number | null;
  newPrice: number | null;
  currency: string;
  change: string;
}

export interface RateNotificationPreview {
  routingPlanId: string;
  routingPlanName: string;
  records: RateCsvRecord[];
}

export const previewRateNotificationApi = async (
  routingPlanIds: string[],
  sendonlyChange = true,
): Promise<RateNotificationPreview[]> => {
  try {
    const response = await axiosInstance.post<RateNotificationPreview[]>(
      "/rated-routing/rate/notification/preview",
      { routingPlanIds, sendonlyChange },
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching rate notification preview.",
    );
  }
};

interface RoutingPlanOptionResponse {
  id: string;
  name: string;
}

export async function fetchRoutingPlansWithRateChange(): Promise<Option[]> {
  try {
    const response = await axiosInstance.get<RoutingPlanOptionResponse[]>(
      "/routing-plans/with-rate-change",
      { params: { sort: "name", size: 10000 } },
    );
    return response.data.map((plan) => ({
      value: plan.id,
      label: plan.name,
    }));
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while retrieving routing plans.",
    );
  }
}

export const useRoutingPlansWithRateChangeQuery = (
  options?: Omit<UseQueryOptions<Option[], Error>, "queryKey" | "queryFn">,
): UseQueryResult<Option[], Error> =>
  useQuery<Option[], Error>({
    queryKey: ["routingPlansWithRateChange"],
    queryFn: fetchRoutingPlansWithRateChange,
    ...options,
  });

export interface BulkRateRouteResponse {
  applied: number;
  failed: number;
  failureReason?: string;
}

export const bulkRateRouteApi = async (
  data: BulkRateRouteRequest,
  ratedRoutingIdsList: string[],
): Promise<BulkRateRouteResponse> => {
  try {
    const response = await axiosInstance.put<BulkRateRouteResponse>(
      "/rated-routing/bulk",
      { ...data, ratedRoutingIds: ratedRoutingIdsList.map(Number) },
    );
    return response.data;
  } catch (error: any) {
    console.error("Error in bulkRateRouteApi:", error);
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while performing bulk rate/route update.",
    );
  }
};

export const bulkMnpHlrApi = async (
  data: BulkMnpHlrRequest,
  ratedRoutingIdsList: string[],
): Promise<void> => {
  try {
    await axiosInstance.put("/rated-routing/bulk-mnp-hlr", {
      ...data,
      ratedRoutingIds: ratedRoutingIdsList.map(Number),
    });
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while performing bulk MNP/HLR update.",
    );
  }
};
export const useRoutingPlanDetailsQuery = (
  params: Params<RoutingPlanDetailsType>,
): UseQueryResult<IPageable<RoutingPlanDetailsType>, Error> => {
  const effectiveParams = {
    ...params,
    sort: params.sort || "createdDate,desc",
  };
  const queryKey = ["routingPlanDetails", JSON.stringify(effectiveParams)];
  return useQuery({
    queryKey,
    queryFn: () => fetchRoutingPlanDetails(effectiveParams),
  });
};

export const useRoutingPlanDetailsCountQuery = (
  params: Params<RoutingPlanDetailsType>,
): UseQueryResult<number, Error> => {
  const queryKey = ["routingPlanDetailsCount", JSON.stringify(params)];
  return useQuery({
    queryKey,
    queryFn: () => fetchRoutingPlanDetailsCount(params),
  });
};

export const useRoutingPlanDetailQuery = (
  id: RoutingPlanDetailsType["id"],
  options?: UseQueryOptions<RoutingPlanDetailsType, Error>,
): UseQueryResult<RoutingPlanDetailsType, Error> => {
  const queryKey = ["routingPlanDetails", id];
  return useQuery({
    queryKey,
    queryFn: () => fetchRoutingPlanDetail(id),
    ...options,
  });
};

export async function fetchRoutingPlanDetailsNoData(
  params: Params<RoutingPlanDetailsType>,
): Promise<IPageable<RoutingPlanDetailsType>> {
  try {
    const response = await axiosInstance.get("/rated-routing", { params });
    const data = response.data;
    return data;
  } catch (error: any) {
    console.error("Error fetching routing plan details:", error);
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while getting the rated-routing details.",
    );
  }
}

export const useNoQuery = (
  params: Params<RoutingPlanDetailsType> = {},
): UseQueryResult<IPageable<RoutingPlanDetailsType>, Error> => {
  const { name = "random text to fetch no result", ...rest } = params;
  const fullParams = { name, ...rest };
  const queryKey = ["routingPlanDetails", JSON.stringify(fullParams)];
  return useQuery({
    queryKey,
    queryFn: () => fetchRoutingPlanDetailsNoData(fullParams),
  });
};

export async function fetchRoutingPlanDetailsHistory(
  id: RoutingPlanDetailsType["id"],
  params: Params<RoutingPlanDetailsHistory> = {},
): Promise<IPageable<RoutingPlanDetailsType>> {
  try {
    const response = await axiosInstance.get(`/instructions/history`, {
      params: { mainId: id, sort: "historyTimestamp,desc", ...params },
    });
    const data = response.data;
    return {
      ...data,
      content: data.content.map((item: any) => ({
        ...item.snapshot,
        historyTimestamp: item.historyTimestamp,
      })),
    };
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while getting the FallBack Routing history for ID ${id}.`,
    );
  }
}

export function getProfitShadeBg(row: any): string | undefined {
  if (typeof row.profit === "number" && row.profit < 0) {
    return "#FF8A80";
  }
  return undefined;
}
